import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon, MapPinOff, Store } from 'lucide-react';
import { WorldPageHeader, WorldSection } from './WorldPageHeader';
import { NpcShopCard } from './NpcShopCard';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { EventChip } from './EventChip';
import { Disclosure } from './Disclosure';
import { CopyLinkButton } from '../guide/CopyLinkButton';
import { useWorld } from '../../contexts/WorldDataContext';
import { npcs } from '../../data/guide/npcs';
import { resolveNpcLocation } from '../../utils/npcLocation';
import { questsServedBy } from '../../utils/npcQuests';
import { NpcQuestServices } from './NpcQuestServices';
import { NPC_LOCATION_GAP, SHOP_INVENTORY_GAP } from '../../data/guide/technicalAudit';
import { imageEntryForMonster } from '../../utils/entityImages';
import { coordLabel, locationCopyText, mapName, mapPath } from '../../utils/worldFormat';
import type { Monster, WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-xl border border-white/10 px-3 text-xs font-semibold text-gray-200 transition-colors duration-150 hover:border-[#D4AF37]/40 hover:text-white';

/**
 * Ficha pública de un NPC. Misma ruta y mismo ID que antes, sin bloques de caza:
 * sin nivel, spots de farmeo, drops, HP ni estadísticas de combate. Los datos originales siguen en los archivos.
 */
export function NpcEntityView({
  w,
  m
}: {
  w: WorldData;
  m: Monster;
}) {
  const world = useWorld();
  const guideNpcs = npcs.filter(n => n.monsterId === m.id);
  const entry = imageEntryForMonster(w, m.id);
  const title = guideNpcs.length === 1 ? guideNpcs[0].name : m.name;
  useEffect(() => {
    document.title = `${title} · NPC y tiendas · Guía MU Vanguard`;
  }, [title]);
  const spawns = w.idx.spawnsByMonster.get(m.id) ?? [];
  const locations = w.idx.locationsByMonster.get(m.id) ?? [];
  return <div data-id="element-1601" data-mp="2kgcq8trnuv2d">
      <WorldPageHeader crumbs={[{
      label: 'Guía',
      to: '/guia'
    }, {
      label: 'NPC y tiendas',
      to: '/guia/npc'
    }, {
      label: title
    }]} eyebrow="NPC" title={title} media={entry?.image ? <EntityImage entry={entry} alt={title} name={title} size="hero" lazy={false} kind="persona" data-id="element-1603" data-mp="fj7j5rkmtezg" /> : undefined} data-id="element-1602" data-mp="2kka6n7mut8ik">
        <p className="mt-3 max-w-2xl text-sm text-gray-300" data-id="element-1604" data-mp="pfamv1v4tloi">
          {guideNpcs.length ? guideNpcs.map(n => n.role).filter((v, i, a) => a.indexOf(v) === i).join(' · ') : questsServedBy(w, m.id).length ? 'Atiende misiones (37_quest_rules).' : 'Función no documentada en los datos de Vanguard.'}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-1605" data-mp="ajuyndh8gk01">
          <CopyLinkButton label="Copiar enlace" data-id="element-1606" data-mp="5pz28fgmb56z" />
        </div>
      </WorldPageHeader>

      <div className="space-y-10" data-id="element-1607" data-mp="3e61yzq66z9gj">
        {guideNpcs.length > 0 ? <WorldSection id="servicios" title={guideNpcs.length > 1 ? 'Ubicaciones y servicios' : 'Ubicación y servicios'} count={guideNpcs.length > 1 ? guideNpcs.length : undefined} data-id="element-1608" data-mp="3a58fd8zs8gtg">
            <div className="grid gap-6 md:grid-cols-2" data-id="element-1609" data-mp="2zzgukch4flwy">
              {guideNpcs.map(n => <NpcShopCard key={n.id} npc={n} loc={resolveNpcLocation(n, {
            status: world.status,
            data: world.data,
            error: world.error
          })} w={w} data-id="element-1610" data-mp="34tuu8r2tgf7f" />)}
            </div>
          </WorldSection> : <>
            <WorldSection id="ubicacion" title="Dónde está" count={spawns.length || undefined} data-id="element-1611" data-mp="3ir9pwdjnxj7u">
              {spawns.length ? <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1612" data-mp="3npkuo9v3p3j7">
                  {spawns.map(s => {
              const coords = coordLabel(s);
              const mn = mapName(w, s.mapId);
              return <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-white/10 p-3.5" data-id="element-1613" data-mp="3duja7rbrspmy">
                        <span className="min-w-0" data-id="element-1614" data-mp="22dt7ii6k2fy3">
                          <span className="block font-semibold text-white" data-id="element-1615" data-mp="22b6q7qtv4m2o">{mn}</span>
                          <span className="block text-sm text-gray-400" data-id="element-1616" data-mp="2im0ijzqbk6rz">{coords ?? 'Sin coordenadas en los archivos'} · aparición configurada en los archivos</span>
                          {s.eventId && <span className="mt-1 block" data-id="element-1617" data-mp="28vo0ce5z8pp0"><EventChip w={w} eventId={s.eventId} data-id="element-1618" data-mp="28ss2epu4q10k" /></span>}
                        </span>
                        <span className="flex flex-wrap gap-2" data-id="element-1619" data-mp="23toy57lz3ytn">
                          <Link to={mapPath(s.mapId)} className={btn} data-id="element-1620" data-mp="31i8s3fqd73t3">
                            <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1621" data-mp="2wjxd6ql7q340" /> Ver mapa
                          </Link>
                          {coords && <CopyTextButton text={locationCopyText(mn, m.name, s)} label="Copiar ubicación" data-id="element-1622" data-mp="2rjswp0kb18ds" />}
                        </span>
                      </li>;
            })}
                </ul> : locations.length ? <ul className="flex flex-wrap gap-2" data-id="element-1623" data-mp="1b6m873vd4wf0">
                  {locations.map(l => <li key={l.id} data-id="element-1624" data-mp="3muzgcggm3r1u">
                      <Link to={mapPath(l.mapId)} className={btn} data-id="element-1625" data-mp="3mw29yw5bk7lg">
                        <MapIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1626" data-mp="3rssq1jh4j36v" /> {mapName(w, l.mapId)}
                      </Link>
                    </li>)}
                </ul> : <p className="flex items-start gap-2 rounded-2xl border border-white/10 p-4 text-gray-300" data-id="element-1627" data-mp="5f13qnmjf3lx">
                  <MapPinOff className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1628" data-mp="acavi9xefrcx" />
                  {NPC_LOCATION_GAP}
                </p>}
            </WorldSection>

            <WorldSection id="servicios" title="Servicios" data-id="element-1629" data-mp="jf2667igaj3i">
              <div className="mb-4" data-id="element-1630" data-mp="oclnxhecxe07">
                <NpcQuestServices w={w} monsterId={m.id} data-id="element-1631" data-mp="obirkty9mjcw" />
              </div>
              <p className="flex items-start gap-2 text-gray-300" data-id="element-1632" data-mp="2l5x7fevffahm">
                <Store className="mt-0.5 h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1633" data-mp="2q473rssw0h4t" />
                {questsServedBy(w, m.id).length ? 'Otros servicios no documentados.' : 'Función no documentada en la configuración.'} {SHOP_INVENTORY_GAP}
              </p>
              <Link to="/guia/npc" className="mt-3 inline-flex font-semibold text-[#D4AF37] underline-offset-4 hover:underline" data-id="element-1634" data-mp="2e3ql0tcsw4tu">Ver NPC y tiendas</Link>
            </WorldSection>
          </>}

        {entry?.image?.origin === 'paquete' && <Disclosure title="Procedencia de la imagen" data-id="element-1635" data-mp="311v52ygkvwxy">
            <p className="text-sm text-gray-300" data-id="element-1636" data-mp="310achedtqo2q">
              Foto de referencia visual del personaje (paquete {entry.key.startsWith('paquete-npc') ? 'VANGUARD_NPC' : 'VANGUARD_MOBS_LIMPIOS'}). Identifica al NPC; no es una captura de Vanguard ni confirma ubicación, inventario, precios ni que esté activo.
              Las fotos de Fanz se limpiaron con IA y pueden tener pequeñas diferencias de detalle.
            </p>
          </Disclosure>}
      </div>
    </div>;
}